import * as React from 'react';
import Box from '@mui/material/Box';
import {
  RichTreeViewPro,
  RichTreeViewProApiRef,
} from '@mui/x-tree-view-pro/RichTreeViewPro';
import { IRapportMallJson } from '../RapportMall';
import { buildItems } from './Utils/RapportMallTreeViewUtils';
import {
  IRapportMallControl,
  isRapportAssociationControl,
  isRapportContentControl,
} from '../RapportMallTypes';

export interface IProps {
  parsedMall: IRapportMallJson | null;
  selectedPath?: number[] | null;
  onSelectPath?: (path: number[] | null) => void;
  onTreeItemTransitionEnd?: () => void; // fires when any item's expand/collapse transition ends
  apiRef?: RichTreeViewProApiRef;
}

export const RapportMallTreeView: React.FC<IProps> = ({
  parsedMall,
  selectedPath,
  onSelectPath,
  onTreeItemTransitionEnd,
  apiRef,
}) => {
  const items = React.useMemo(() => buildItems(parsedMall), [parsedMall]);

  const getControlAtPath = React.useCallback(
    (
      mall: IRapportMallJson | null,
      path?: number[] | null
    ): IRapportMallControl | null => {
      if (!mall || !path || path.length === 0) return null;
      let controls = mall.Body || [];
      let current: IRapportMallControl | undefined;
      for (let i = 0; i < path.length; i += 1) {
        const idx = path[i];
        current = controls[idx];
        if (i === path.length - 1) break;
        if (isRapportContentControl(current)) {
          controls = current.Content || [];
        } else if (isRapportAssociationControl(current)) {
          controls = current.Template || [];
        } else {
          return null;
        }
      }
      return current ?? null;
    },
    []
  );

  const isSelectedContainer = React.useMemo(() => {
    const ctrl = getControlAtPath(parsedMall, selectedPath);
    if (!ctrl) return false;
    if (isRapportContentControl(ctrl)) return (ctrl.Content?.length || 0) > 0;
    if (isRapportAssociationControl(ctrl))
      return (ctrl.Template?.length || 0) > 0;
    return false;
  }, [getControlAtPath, parsedMall, selectedPath]);

  const toAncestorIds = React.useCallback(
    (path?: number[] | null, includeFull: boolean = true) => {
      const ids: string[] = items.length > 0 ? ['root'] : [];
      if (!path || path.length === 0) return ids;
      const acc: number[] = [];
      path.forEach((n, i) => {
        acc.push(n);
        const isLast = i === path.length - 1;
        if (!isLast || includeFull) ids.push(acc.join('-'));
      });
      return ids;
    },
    [items]
  );

  const defaultExpandedItems = React.useMemo(
    () => toAncestorIds(selectedPath, !isSelectedContainer),
    [toAncestorIds, selectedPath, isSelectedContainer]
  );
  const [expandedItems, setExpandedItems] =
    React.useState<string[]>(defaultExpandedItems);
  React.useEffect(() => {
    const needed = toAncestorIds(selectedPath, !isSelectedContainer);
    setExpandedItems(prev => Array.from(new Set([...(prev || []), ...needed])));
  }, [selectedPath, toAncestorIds, isSelectedContainer]);
  const selectedId = React.useMemo(
    () =>
      selectedPath && selectedPath.length > 0 ? selectedPath.join('-') : 'root',
    [selectedPath]
  );

  const handleExpandedItemsChange = React.useCallback(
    (_e: React.SyntheticEvent<Element, Event> | null, ids: string[]) => {
      // Update expanded items based on icon container clicks or keyboard toggles
      setExpandedItems(ids || []);
    },
    []
  );

  const handleSelectedItemsChange = React.useCallback(
    (_event: unknown, idParam: string | null) => {
      if (!onSelectPath) return;
      const id = idParam || undefined;
      if (!id || id === 'root') {
        onSelectPath(null);
        return;
      }
      const path = id
        .split('-')
        .filter(Boolean)
        .map(s => Number(s))
        .filter(n => !Number.isNaN(n));
      onSelectPath(path.length ? path : null);
    },
    [onSelectPath]
  );

  return (
    <Box sx={{ width: '100%', minHeight: 200, userSelect: 'none' }}>
      <RichTreeViewPro
        items={items}
        defaultExpandedItems={defaultExpandedItems}
        expandedItems={expandedItems}
        onExpandedItemsChange={handleExpandedItemsChange}
        expansionTrigger="iconContainer"
        selectedItems={selectedId}
        onSelectedItemsChange={handleSelectedItemsChange}
        aria-label="Rapportmall struktur"
        apiRef={apiRef}
        slotProps={{
          root: {
            onTransitionEnd: () => {
              onTreeItemTransitionEnd?.();
            },
          },
        }}
      />
    </Box>
  );
};
